/*
 * Where every migrated area is wired into the data-action registry.
 *
 * Kept out of main.ts so it can be exercised without main.ts's boot side
 * effects: ClientApp/test/person-wiring.test.ts calls registerAllAreas() and
 * then resolves every data-action a Razor view carries. A handler that is
 * defined but not REGISTERED under the path the markup names is a button that
 * silently does nothing, and the older text-scanning guard in Web.Test only
 * proves the method exists somewhere, not that the path reaches it.
 */
import { registerArea } from '../core/registry';
import { TopMenu } from '../core/nav';
import * as Person from './person';
import { Claim as PersonClaim } from './person-claim';
import { Domain as PersonDomain, Role as PersonRole } from './person-access';
import * as Application from './application';
import * as Domain from './domain';
import * as AppGroup from './appgroup';
import * as Role from './role';
import * as Resource from './resource';
import * as ResourcePolicy from './resource-policy';
import { ClaimType } from './claim-type';
import { ExternalValues, ValueType } from './claim-value-type';
import * as ClaimTemplate from './claim-template';
import * as Invite from './invite';
import * as License from './license';
import { Binder as LicenseBinder } from './license-binder';
import { Quota as LicenseQuota } from './license-quota';
import * as LicenseTemplate from './license-template';
import { List as FeatureList, Resolve as FeatureResolve } from './feature';
import { Bindings as FeatureBindings } from './feature-bindings';
import { Rollout as FeatureRollout } from './feature-rollout';
import * as Certificate from './certificate';
import * as SamlPeer from './samlpeer';
import { ChangePassword } from './home';
import { Tab as PersonTenantTab } from './person-tenant';
import { Detail as RegistrationDetail, List as RegistrationList } from './registration-request';
import { List as ChallengeQuestionList } from './challenge-question';
import { Create as PolicyCreate, Edit as PolicyEdit, List as PolicyList } from './person-policy';
import { Detail as PersonGroupDetail, List as PersonGroupList } from './person-group';
import { Client as IdentityClient } from './identity';
import { Applications as TenantApplications, List as TenantList, Members as TenantMembers } from './tenant';
import { Detail as ExportTemplateDetail, List as ExportTemplateList } from './export-template';
import { Session as ImportSession } from './import';
import { List as EntityList } from './entity';
import { List as BillingList, Domain as BillingDomain, Plan as BillingPlan, Provider as BillingProvider, Settings as BillingSettings } from './billing';
import { Shared } from './shared';
import { AccessChange, AppLog, ApplicationLog } from './logs';
import { EntityPicker } from '../components/entity-picker';

/** Area roots whose every data-action path must resolve through the registry. */
export const MIGRATED_AREAS = [
    'Application', 'AppGroup', 'Billing', 'Certificate', 'ChallengeQuestion', 'Claim', 'ClaimTemplate', 'Domain', 'Entity',
    'ExportTemplate', 'Feature', 'Identity', 'Import', 'Invite', 'License', 'LicenseTemplate', 'Logs',
    'Person', 'PersonGroup', 'PersonTenant', 'Picker', 'RegistrationRequest', 'Resource', 'Role', 'Shared',
    'SamlPeer', 'Tenant',
] as const;

/**
 * Paths under a migrated root that are still served by legacy JS through the
 * registry's window fallback. Empty since Phase 3.3 migrated Person Policy:
 * every path under a registered root now resolves through the registry.
 */
export const WINDOW_FALLBACK_PREFIXES: readonly string[] = [];

export function registerAllAreas(): void {
    // Home is the rail (core/nav) plus the Manage Account screen. Registering
    // the root turns the registry's window fallback OFF beneath it, so every
    // Home.* handler has to be named here.
    registerArea('Home', { TopMenu, ChangePassword });

    // Person is fully migrated (Phase 3.3): no wwwroot/js/Person/*.js is left,
    // so no window mirror is needed -- every caller reaches these through
    // data-action and the registry.
    registerArea('Person', {
        List: Person.List,
        Dashboard: Person.Dashboard,
        Create: Person.Create,
        Detail: Person.Detail,
        Edit: Person.Edit,
        ResetPassword: Person.ResetPassword,
        Remediate: Person.Remediate,
        Image: Person.Image,
        PolicyAssign: Person.PolicyAssign,
        AppConsent: Person.AppConsent,
        Templates: Person.Templates,
        Challenge: Person.Challenge,
        Claim: PersonClaim,
        Role: PersonRole,
        Domain: PersonDomain,
        // The policy ADMIN screen, which hangs off the same root but is a
        // different area: Person.PolicyAssign above is the policy card on
        // Person Detail, which only picks one of these.
        Policy: {
            List: PolicyList,
            Create: PolicyCreate,
            Edit: PolicyEdit,
        },
    });

    // The Phase 3.3 areas that came before it. data-action resolves through the
    // registry from any view: the ApplicationRole partials reach
    // Application.Role.* whether they were opened from an Application's Roles
    // tab or from /Role.
    registerArea('Application', {
        List: Application.List,
        Create: Application.Create,
        Detail: Application.Detail,
        Resource: Application.Resource,
        Role: Application.Role,
        Person: Application.Person,
        Profile: Application.Profile,
    });
    registerArea('AppGroup', {
        List: AppGroup.List,
        Create: AppGroup.Create,
        Edit: AppGroup.Edit,
        Applications: AppGroup.Applications,
    });
    // Role.Edit.Show / SaveClick are reached from the ApplicationRole partials
    // as well as from /Role: role editing has one implementation wherever the
    // detail modal was opened.
    registerArea('Role', {
        List: Role.List,
        Create: Role.Create,
        Edit: Role.Edit,
    });
    // Resource is GLOBAL resources -- parentless rows, true global and
    // domain-level. Application-scoped ones belong to Application.Resource.
    // Resource.Edit.{Add,Remove}Certificate are also reached from the
    // Application detail Certificates tab, which renders the same shared panel.
    registerArea('Resource', {
        List: Resource.List,
        Create: Resource.Create,
        Edit: Resource.Edit,
        Scope: Resource.Scope,
        // Resource Policies are a separate screen that hangs off the same root.
        // Registering `Resource` turns off the registry's window fallback for
        // everything beneath it, so Policy has to be registered here or every
        // ResourcePolicy button goes dead -- the wiring guard proves it.
        Policy: {
            List: ResourcePolicy.List,
            Create: ResourcePolicy.Create,
            Edit: ResourcePolicy.Edit,
        },
    });
    // The claim vocabulary. `Claim` is the legacy root the views already name;
    // the Person Detail grants tabs are Person.Claim, a different area that only
    // looks similar. Claim.ClaimValue is absent on purpose: its one handler
    // (NewClick) was dead -- no view referenced it and Claim.ExternalValues
    // replaced it -- so wwwroot/js/Claim/ClaimValue.js went rather than moved.
    registerArea('Claim', {
        ClaimType,
        ValueType,
        ExternalValues,
    });
    registerArea('ClaimTemplate', {
        List: ClaimTemplate.List,
        Detail: ClaimTemplate.Detail,
    });
    // Invitations: one screen for issue + resend + cancel. Issue used to be a
    // full-page form on PersonController, so the legacy area had no handler for
    // it at all.
    registerArea('Invite', {
        List: Invite.List,
        Issue: Invite.Issue,
    });
    // The License screens. Binder and Quota are separate modules because they
    // are separate editors, but they are tabs of the one License Detail modal
    // and register under the one root the views name.
    registerArea('License', {
        List: License.List,
        Detail: License.Detail,
        Binder: LicenseBinder,
        Quota: LicenseQuota,
    });
    registerArea('LicenseTemplate', {
        List: LicenseTemplate.List,
        Detail: LicenseTemplate.Detail,
    });
    // Feature flags. One area for two pages -- the global Features screen and the
    // Features tab of an Application -- and one detail modal whose Bindings and
    // Rollout tabs are separate modules because they are separate editors.
    registerArea('Feature', {
        List: FeatureList,
        Resolve: FeatureResolve,
        Bindings: FeatureBindings,
        Rollout: FeatureRollout,
    });
    // Certificates. Three screens -- the domain list, the expiring list and the
    // import dialog -- and one detail modal, whose Domains tab writes a binding
    // per checkbox flip.
    registerArea('Certificate', {
        List: Certificate.List,
        Detail: Certificate.Detail,
        Import: Certificate.Import,
    });
    // SAML peers. The detail dialog's per-row writes (application bindings,
    // attribute mappings) re-fetch the dialog, because the reach warnings it
    // renders are computed server-side from the saved state.
    registerArea('SamlPeer', {
        List: SamlPeer.List,
        Detail: SamlPeer.Detail,
    });
    // Password-reset challenge questions: the Domain's own plus the global
    // catalogue, on one screen.
    registerArea('ChallengeQuestion', { List: ChallengeQuestionList });
    // Person groups: the groups of a Domain, with member and Domain-scope
    // editors in one detail dialog.
    registerArea('PersonGroup', { List: PersonGroupList, Detail: PersonGroupDetail });
    // The OpenIddict registration behind an Application, on its Identity tab,
    // plus the machine roles its client_credentials tokens carry.
    registerArea('Identity', { Client: IdentityClient });
    // Tenants: the groupings a multi-tenant Application keys its tenant_id
    // claim on, with member and Application-binding tabs.
    registerArea('Tenant', {
        List: TenantList,
        Members: TenantMembers,
        Applications: TenantApplications,
    });
    // Export templates. The views used to name Mcp.Admin.ExportTemplate.*;
    // registering `Mcp` as an area would turn the registry's window fallback
    // off for every remaining Mcp.Admin.* name, so the area has its own root.
    registerArea('ExportTemplate', { List: ExportTemplateList, Detail: ExportTemplateDetail });
    // The import wizard's session panel, including its client-side paging.
    registerArea('Import', { Session: ImportSession });
    // The Tenants tab of Person Detail. A separate root from Person: it is the
    // PersonTenant controller's surface, and Person Detail merely hosts it.
    registerArea('PersonTenant', { Tab: PersonTenantTab });
    // The Entities tab of Application Detail: the entity classes and entities an
    // application registered (ENTITY-DESIGN.md §6), read-only and paged.
    registerArea('Entity', { List: EntityList });
    // The Billing tab of Application Detail: subscriptions and the License each
    // keeps, and "Return to Billing" for one an operator holds (Billing Phase 6).
    // The Domain Billing area (rail): overview, subscriptions, plans, providers, webhook
    // events and settings -- the catalog an operator configures without a deploy.
    registerArea('Billing', { List: BillingList, Domain: BillingDomain, Provider: BillingProvider, Plan: BillingPlan, Settings: BillingSettings });
    // The consumer-app registration queue, rendered into an Application's
    // Registrations tab.
    registerArea('RegistrationRequest', { List: RegistrationList, Detail: RegistrationDetail });
    // The three log browsers -- the console's own AppLog, a consumer app's log
    // and the permission audit trail. One root: they share a cursor pager, a
    // filter form and a row-detail toggle, and differ only in what they read.
    registerArea('Logs', { AccessChange, AppLog, ApplicationLog });
    // The shared entity picker modal. Feature, License, PersonGroup, SamlPeer
    // and Tenant all open it; it belongs to none of them.
    registerArea('Picker', EntityPicker);
    // View helpers that belong to no one area.
    registerArea('Shared', Shared);
    registerArea('Domain', {
        Index: Domain.Index,
        Create: Domain.Create,
        Edit: Domain.Edit,
        Detail: Domain.Detail,
        Applications: Domain.Applications,
        Person: Domain.Person,
    });
}
